import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import RecordRTC, { StereoAudioRecorder } from 'recordrtc';

export type MicrophoneProps = {
  onAudioCaptured: (b: ArrayBuffer) => void;
  onStartRecording?: () => void;
  onStopRecording?: () => void;
  onError: (message: string) => void;
  timeSlice?: number;
  sampleRate?: 8000 | 16000 | 32000 | 44100 | 48000;
  channelCount?: 1 | 2;
};

export const useMicrophone = ({
  onAudioCaptured,
  onStartRecording,
  onStopRecording,
  onError,
  timeSlice = 100,
  sampleRate = 48000,
  channelCount = 1,
}: MicrophoneProps) => {
  const [isMuted, setIsMuted] = useState(false);
  const isMutedRef = useRef(isMuted);

  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioContext = useRef<AudioContext | null>(null);
  const recorder = useRef<RecordRTC | null>(null);

  const sendAudio = useRef(onAudioCaptured);
  sendAudio.current = onAudioCaptured;

  const generation = useRef(0);
  const mounted = useRef(true);
  const releaseResources = useCallback(() => {
    generation.current += 1;
    const stream = mediaStreamRef.current;
    const context = audioContext.current;
    const activeRecorder = recorder.current;
    mediaStreamRef.current = null;
    audioContext.current = null;
    recorder.current = null;
    stream?.getTracks().forEach(track => track.stop());
    if (context) void context.close().catch(() => {});
    activeRecorder?.stopRecording();
  }, []);

  /**
   * Start recording the user input audio.
   *
   * Resolves to whether capture actually started. The caller needs to know:
   * a web call must not begin without a working microphone, and a denied
   * permission prompt is the common case rather than an exotic failure.
   */
  const start = useCallback(async (): Promise<boolean> => {
    releaseResources();
    const attempt = generation.current;
    const isCurrent = () => mounted.current && attempt === generation.current;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate,
          channelCount,
          noiseSuppression: true,
          echoCancellation: true,
        },
      });

      if (!isCurrent()) {
        stream.getTracks().forEach(track => track.stop());

        return false;
      }
      mediaStreamRef.current = stream;
      isMutedRef.current = false;
      setIsMuted(false);

      const context = new AudioContext();
      audioContext.current = context;

      // Initialize RecordRTC with WAV format
      recorder.current = new RecordRTC(mediaStreamRef.current, {
        type: 'audio',
        mimeType: 'audio/wav',
        recorderType: StereoAudioRecorder,
        numberOfAudioChannels: channelCount,
        sampleRate,
        desiredSampRate: sampleRate,
        timeSlice,
        ondataavailable: async (blob: Blob) => {
          try {
            /**
             * `mute()` sets `track.enabled = false`, which makes the track
             * emit SILENCE — it does not stop RecordRTC producing chunks. So
             * without this the caller kept streaming (and paying Gemini input
             * tokens for) digital silence while muted.
             */
            if (!isCurrent() || isMutedRef.current) {
              return;
            }

            // Convert to array buffer and send to handler
            const buffer = await blob.arrayBuffer();
            if (isCurrent() && !isMutedRef.current) sendAudio.current?.(buffer);
          } catch (err) {
            console.error('Error processing RecordRTC audio chunk', err);
            onError(
              `Error processing audio: ${err instanceof Error ? err.message : String(err)}`,
            );
          }
        },
      });

      // Start recording
      recorder.current.startRecording();
      onStartRecording?.();

      return true;
    } catch (e) {
      if (!isCurrent()) return false;
      releaseResources();
      const message = e instanceof Error ? e.message : 'Unknown error';
      onError(`Error starting microphone: ${message}`);
      console.error(e);

      return false;
    }
  }, [
    sampleRate,
    channelCount,
    timeSlice,
    onStartRecording,
    onError,
    releaseResources,
  ]);

  /**
   * Stop recording the user input audio.
   */
  const stop = useCallback(() => {
    try {
      releaseResources();
      isMutedRef.current = false;

      onStopRecording?.();
      setIsMuted(false);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      onError(`Error stopping microphone: ${message}`);
      console.error(e);
    }
  }, [onError, onStopRecording, releaseResources]);

  /**
   * Mute the microphone
   */
  const mute = useCallback(() => {
    mediaStreamRef.current?.getTracks().forEach(track => {
      track.enabled = false;
    });

    isMutedRef.current = true;
    setIsMuted(true);
  }, [mediaStreamRef]);

  /**
   * Unmute the microphone
   */
  const unmute = useCallback(() => {
    mediaStreamRef.current?.getTracks().forEach(track => {
      track.enabled = true;
    });

    isMutedRef.current = false;
    setIsMuted(false);
  }, [mediaStreamRef]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
      releaseResources();
    };
  }, [releaseResources]);

  return useMemo(
    () => ({
      start,
      stop,
      mute,
      unmute,
      isMuted,
    }),
    [start, stop, mute, unmute, isMuted],
  );
};
